import { forwardRef, useEffect, useRef, useState } from "react";
import ICONS from "../../../assets/Icons";

/**
 * @typedef {("verified" | "error" | "typing" | "default")} TypeVariants
 * @typedef {("big" | "small")} FontVariants
 * @typedef {("dark" | "light")} EyeVariants
 */
/**
 * @typedef PropType
 * @type {object}
 * @property {string} placeholder - Text to show in the placeholder
 * @property {TypeVariants} variant - one of "error", "verified", "typing", or "default"
 * @property {string} bgColor - background colour of element that this input field is on top of (var only)
 * @property {string} fgColor - The foreground colour of this input field. (var only)
 * @property {React.FormEventHandler<HTMLInputElement>} [onInput] - This function is given to "onInput" of input tag
 * @property {string} [value] - The value attribute of the input tag
 * @property {string} [name] - The value of the name attribute of the underlying <input>
 * @property {FontVariants} [fontKind="big"] - Use "big" font or "small" font. defaults to big.
 * @property {EyeVariants} [eyeKind="light"] - Use "dark" or "light" eye icon. defaults to light.
 * @property {Boolean} isDisabled - Whether this input field is disabled.
 * @property {Boolean} isFullWidth - Whether this input field is full width.
 */
/**
 * @type {React.ForwardRefRenderFunction<HtmlHTMLInputElement, PropType>}
 */
export default forwardRef(function (
  {
    placeholder,
    variant,
    bgColor,
    fgColor,
    onInput,
    value,
    fontKind,
    name,
    eyeKind,
    isDisabled,
    isFullWidth,
  },
  inputBoxRef,
) {
  const [variantInfo, setVariantInfo] = useState({
    color: "var(--text-primary)",
    adornment: null,
    fontKind:
      fontKind && fontKind === "small" ? "var(--font-xs)" : "var(--font-md)",
    adornmentDimension:
      fontKind && fontKind === "small" ? "var(--icon-sm)" : "var(--icon-md)",
  });
  const [oldVariant, setOldVariant] = useState({});

  useEffect(() => {
    let vi;
    switch (variant) {
      case "verified":
        vi = {
          ...variantInfo,
          color: "var(--positive-act)",
        };
        break;
      case "error":
        vi = {
          ...variantInfo,
          color: "var(--negative-act)",
        };
        break;
      case "typing":
        vi = {
          ...variantInfo,
          color: "var(--pending)",
        };
        break;
      default:
        vi = {
          ...variantInfo,
          color: fgColor ?? "var(--text-primary)",
        };
        break;
    }
    setVariantInfo(vi);
    setOldVariant(vi);
  }, [variant]);

  const [passwordShown, setPasswordShown] = useState(false);

  const [PasswordShown, PasswordHidden] = [
    (eyeKind ?? "light") === "dark"
      ? ICONS?.PASSWORD_HIDDEN_DARKGREY
      : ICONS?.PASSWORD_HIDDEN_LIGHTGREY,
    (eyeKind ?? "light") === "dark"
      ? ICONS?.PASSWORD_VISIBLE_DARKGREY
      : ICONS?.PASSWORD_VISIBLE_LIGHTGREY,
  ];

  useEffect(() => {
    setVariantInfo((i) => {
      let vi = {
        ...i,
        fontKind:
          fontKind && fontKind === "small"
            ? "var(--font-xs)"
            : "var(--font-md)",
        adornmentDimension:
          fontKind && fontKind === "small"
            ? "var(--icon-sm)"
            : "var(--icon-md)",
      };
      setOldVariant(vi);
      return vi;
    });
  }, [fontKind]);

  useEffect(() => {
    if (value) {
      setIsFocused(true);
    }
  }, [value]);

  const iRef = useRef(null);
  inputBoxRef ??= iRef;

  const [isFocused, setIsFocused] = useState(false);
  const placeHolderRef = useRef(null);
  return (
    <div
      onClick={(e) => {
        if (isDisabled) return;
        e.preventDefault();
        e.stopPropagation();
        if (inputBoxRef?.current?.value) {
          setIsFocused(true);
          return;
        }
        setIsFocused(true);
        inputBoxRef?.current?.focus();
      }}
      className={`border flex flex-row items-center py-padding-sm px-padding-md justify-between gap-gap-md rounded-full w-full relative ${
        isFullWidth ? "" : "max-w-input-max-width min-w-input-min-width"
      } `}
      role="input"
      style={{
        fontSize: variantInfo.fontKind,
        borderColor: variantInfo.color,
        opacity: isDisabled ? "var(--light-opacity)" : "var(--base-opacity)",
        cursor: isDisabled ? "not-allowed" : "default",
      }}
    >
      <div
        className="transition-all px-padding-xs"
        ref={placeHolderRef}
        style={{
          userSelect: "none",
          backgroundColor: bgColor,
          color: variantInfo.color,
          transform: isFocused
            ? "translateY(calc(-2*var(--padding-sm)))"
            : "translateY(0)",
          fontSize: isFocused ? "var(--font-xs)" : variantInfo.fontKind,
          position: "absolute",
        }}
      >
        {placeholder}
      </div>
      <input
        disabled={isDisabled}
        value={value}
        onInput={onInput}
        onFocus={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsFocused(true);
          setVariantInfo({
            ...variantInfo,
            color: "var(--pending)",
          });
        }}
        onBlur={() => {
          setVariantInfo(oldVariant);
          if (inputBoxRef?.current?.value) {
            return;
          }
          setIsFocused(false);
        }}
        ref={inputBoxRef}
        name={name ?? "inputBox"}
        style={{
          fontSize: variantInfo.fontKind,
          color: fgColor ?? variantInfo.color,
          backgroundColor: bgColor,
          cursor: isDisabled ? "not-allowed" : "default",
          userSelect: isDisabled ? "none" : "auto",
        }}
        type={passwordShown ? "text" : "password"}
        className="w-full"
      />
      <div
        style={{
          width: variantInfo.adornmentDimension,
          height: variantInfo.adornmentDimension,
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setPasswordShown((x) => !x);
        }}
      >
        <img src={!passwordShown ? PasswordShown : PasswordHidden}></img>
      </div>
    </div>
  );
});
